import React, { useState, useEffect, useRef } from 'react';
import { fotosService } from '../../services/fotos';
import toast from 'react-hot-toast';
import { Camera, X, Loader2 } from 'lucide-react';

/**
 * Galería de fotos adjuntas a la respuesta de un ítem/pregunta de inspección
 * (guardadas en S3, varias fotos por ítem). Funciona igual en cualquier
 * módulo: solo cambia `modulo` (y `ct` cuando aplica).
 */
const GaleriaFotos = ({ modulo, itemId, sesion, ct = null, soloLectura = false }) => {
    const [fotos, setFotos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [subiendo, setSubiendo] = useState(false);
    const inputRef = useRef(null);

    const cargar = async () => {
        try {
            const data = await fotosService.listar(modulo, itemId, sesion, ct);
            setFotos(data);
        } catch {
            /* silencioso: no bloquea el ítem si falla */
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, [modulo, itemId, sesion, ct]);

    const handleSeleccionar = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setSubiendo(true);
        try {
            for (const file of files) {
                const foto = await fotosService.subir(file, { modulo, itemId, sesion, ct });
                setFotos((prev) => [...prev, foto]);
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Error al subir la foto');
        } finally {
            setSubiendo(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const handleEliminar = async (fotoId) => {
        if (!window.confirm('¿Eliminar esta foto?')) return;
        try {
            await fotosService.eliminar(fotoId);
            setFotos((prev) => prev.filter((f) => f.id !== fotoId));
        } catch {
            toast.error('Error al eliminar la foto');
        }
    };

    if (cargando) return null;

    return (
        <div className="flex items-center gap-2 flex-wrap mt-2">
            {fotos.map((foto) => (
                <div key={foto.id} className="relative group">
                    <a href={foto.url} target="_blank" rel="noopener noreferrer">
                        <img
                            src={foto.url}
                            alt="Foto adjunta"
                            className="h-14 w-14 object-cover rounded-lg border border-gray-200"
                        />
                    </a>
                    {!soloLectura && (
                        <button
                            onClick={() => handleEliminar(foto.id)}
                            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Eliminar foto"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </div>
            ))}

            {!soloLectura && (
                <button
                    onClick={() => inputRef.current?.click()}
                    disabled={subiendo}
                    className="h-14 w-14 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors disabled:opacity-60"
                    title="Adjuntar foto"
                >
                    {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                    <span className="text-[9px] mt-0.5">{subiendo ? '...' : 'Foto'}</span>
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleSeleccionar}
                className="hidden"
            />
        </div>
    );
};

export default GaleriaFotos;
